import { LoadFailed } from '@/components/LoadFailed'
import { ApplicationRejectedScreen } from '@/components/onboarding/ApplicationRejectedScreen'
import { OwnerOperatorDetailsFields } from '@/components/onboarding/OwnerOperatorDetailsFields'
import { OnboardingLayout } from '@/components/OnboardingLayout'
import { TokenError } from '@/components/TokenError'
import { useSubmitBasicInfo } from '@/hooks/mutations/onboarding/useSubmitBasicInfo'
import { useFmcsaLookup } from '@/hooks/queries/onboarding/useFmcsaLookup'
import { useDebouncedDotNumber } from '@/hooks/useDebouncedDotNumber'
import { useTokenValidation } from '@/hooks/queries/onboarding/useTokenValidation'
import { getErrorMessage } from '@/lib/queries'
import { mapTokenErrorMessage, type TokenErrorReason } from '@/lib/tokenErrors'
import { createHonoClient } from '@/stores/api'
import { setPendingTenantSelection, setSessionToken, setTenant } from '@/stores/session'
import { standardSchemaResolver } from '@hookform/resolvers/standard-schema'
import {
  isOwnerOperatorFlow,
  isTerminalOnboardingPhase,
  ONBOARDING_FLOW_KEYS,
  type OnboardingFlowKey,
} from '@repo/constants'
import { Button, CenteredLoading } from '@repo/ui/components'
import { createFileRoute, useNavigate } from '@tanstack/react-router'
import { LoaderCircleIcon } from 'lucide-react'
import { useEffect, useMemo, useState } from 'react'
import { useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { z } from 'zod'
import {
  type BasicInfoFormData,
  makeBasicInfoFormSchema,
  toDeclaredVehicles,
  toVehicleFormValues,
} from '@/components/onboarding/basicInfoSchema'

export const Route = createFileRoute('/onboard/$flow/$token/')({
  component: OnboardBasicInfoPage,
})

const api = createHonoClient(null)

function UsedInviteSignIn({ token }: { token: string }) {
  const navigate = useNavigate()
  const [failed, setFailed] = useState(false)

  useEffect(() => {
    const open = async () => {
      const res = await api['portal-auth'].driver['invite-sign-in'].$post({ json: { token } })
      const body = await res.json()
      if (!('sessionToken' in body)) {
        setPendingTenantSelection({ tempToken: body.tempToken, tenants: body.tenants })
        return navigate({ to: '/carriers', replace: true })
      }
      setSessionToken(body.sessionToken)
      setTenant(body.tenant)
      window.location.href = `/carriers/${body.tenant.slug}/driver-onboarding/dashboard`
    }
    open().catch(() => setFailed(true))
  }, [token, navigate])

  return failed ? <TokenError reason="used" /> : <CenteredLoading />
}

// prefillFormData comes back as JSONB. Nothing on the wire enforces its shape,
// so parse defensively.
const prefillFormDataSchema = z.object({
  dateOfBirth: z.string().optional(),
  cdlNumber: z.string().optional(),
  dotNumber: z.string().optional(),
  companyName: z.string().optional(),
  mcNumber: z.string().optional(),
  truckCount: z.number().optional(),
  hasMultipleTrucks: z.boolean().optional(),
  serviceArea: z.string().optional(),
  companyLegalName: z.string().optional(),
  ein: z.string().optional(),
  hasCompany: z.boolean().optional(),
  physicalAddress: z.string().optional(),
})

function isFlowKey(v: string): v is OnboardingFlowKey {
  return (ONBOARDING_FLOW_KEYS as readonly string[]).includes(v)
}

function OnboardBasicInfoPage() {
  const { t } = useTranslation('ui')
  const { flow: rawFlow, token } = Route.useParams()
  const flow = isFlowKey(rawFlow) ? rawFlow : null
  const tokenQuery = useTokenValidation(flow ?? 'standard_driver_onboarding', token)
  const { data, isLoading } = tokenQuery
  // A verdict the SUBMIT came back with, for the window between the office
  // revoking and this tab knowing — the validation above passed when the page
  // loaded. It lives here rather than in the form because `TokenError` renders
  // its own `OnboardingLayout`, and the form is already inside one: returning it
  // from there stacked a second header inside the first (PET-3831).
  const [submitTokenError, setSubmitTokenError] = useState<TokenErrorReason | null>(null)

  if (!flow) {
    return <TokenError reason="not_found" />
  }

  if (isLoading) {
    return (
      <OnboardingLayout>
        <CenteredLoading />
      </OnboardingLayout>
    )
  }

  // A failed REQUEST, not a verdict on the link. `/validate/:token` answers an
  // invalid token with a 200 carrying `valid: false`, so anything thrown here is
  // transport — most often a 429 off the 60-per-minute budget this wizard shares
  // with the carrier one. Collapsed into `not_found` it told an applicant whose
  // link was fine that it did not exist, with no way forward.
  if (tokenQuery.isError) {
    return (
      <LoadFailed
        reason={getErrorMessage(tokenQuery.error, t('onboarding.error.loadFailed'))}
        onRetry={() => void tokenQuery.refetch()}
        isRetrying={tokenQuery.isFetching}
      />
    )
  }

  if (!data || !data.valid) {
    // Narrow the engine's reason enum to the ones TokenError has copy for —
    // `flow_unknown` and anything added later collapse to 'not_found'.
    const KNOWN_REASONS: readonly TokenErrorReason[] = [
      'expired',
      'used',
      'revoked',
      'tenant_inactive',
      'not_found',
    ]
    const rawReason = data && 'reason' in data ? data.reason : 'not_found'
    const reason: TokenErrorReason = (KNOWN_REASONS as readonly string[]).includes(rawReason)
      ? (rawReason as TokenErrorReason)
      : 'not_found'
    // The link stays a way in after it made the account (`AUTH-40`): opening it
    // again signs its owner in.
    if (reason === 'used') return <UsedInviteSignIn token={token} />
    return <TokenError reason={reason} />
  }

  if (submitTokenError) {
    return <TokenError reason={submitTokenError} />
  }

  const app = data.application as
    | (Record<string, unknown> & {
        applicantFirstName?: string | null
        applicantLastName?: string | null
        applicantEmail?: string | null
        applicantPhone?: string | null
        prefillFormData?: unknown
        phase?: string | null
        rejectionReason?: string | null
      })
    | null

  // The link the applicant already had keeps pointing at the application the
  // office rejected — the replacement token from the rejection email points at
  // the clone (`ONB-08`). Answering the old one with the form again is what left
  // a rejected applicant with no feedback at all (PET-3816).
  if (isTerminalOnboardingPhase(app?.phase)) {
    return (
      <OnboardingLayout>
        <ApplicationRejectedScreen reason={app?.rejectionReason} />
      </OnboardingLayout>
    )
  }

  const prefillParse = prefillFormDataSchema.safeParse(app?.prefillFormData)
  const prefill = prefillParse.success ? prefillParse.data : null
  // Typed by the RPC client, unlike `prefillFormData` — these are real columns.
  const vehicles = data.vehicles ?? []

  return (
    <OnboardingLayout>
      {data.priorRejectionReason && (
        <div className="mb-6 rounded-md border border-amber-300 bg-amber-50 p-4">
          <p className="text-sm font-medium text-amber-900">{t('onboarding.resubmit.title')}</p>
          <p className="mt-1 text-sm text-amber-800">{t('onboarding.resubmit.body')}</p>
          <blockquote className="mt-2 border-l-4 border-amber-400 pl-3 text-sm text-amber-900 italic">
            {data.priorRejectionReason}
          </blockquote>
        </div>
      )}
      <BasicInfoForm
        flow={flow}
        token={token}
        tenantName={data.tenant?.name ?? null}
        onTokenError={setSubmitTokenError}
        defaultValues={{
          firstName: app?.applicantFirstName ?? '',
          lastName: app?.applicantLastName ?? '',
          email: app?.applicantEmail ?? '',
          phone: app?.applicantPhone ?? '',
          dateOfBirth: prefill?.dateOfBirth ?? '',
          cdlNumber: prefill?.cdlNumber ?? '',
          dotNumber: prefill?.dotNumber ?? '',
          companyName: prefill?.companyName ?? '',
          mcNumber: prefill?.mcNumber ?? '',
          truckCount: prefill?.truckCount,
          hasMultipleTrucks: prefill?.hasMultipleTrucks,
          serviceArea: prefill?.serviceArea ?? '',
          companyLegalName: prefill?.companyLegalName ?? '',
          ein: prefill?.ein ?? '',
          hasCompany: prefill?.hasCompany,
          physicalAddress: prefill?.physicalAddress ?? '',
          vehicles: toVehicleFormValues(vehicles),
        }}
      />
    </OnboardingLayout>
  )
}

function BasicInfoForm({
  flow,
  token,
  tenantName,
  defaultValues,
  onTokenError,
}: {
  flow: OnboardingFlowKey
  token: string
  tenantName: string | null
  defaultValues: BasicInfoFormData
  // Raised, not rendered: the page owns the token-error screen so it replaces
  // this form's whole layout instead of appearing inside it.
  onTokenError: (reason: TokenErrorReason) => void
}) {
  const { t } = useTranslation('ui')
  const navigate = useNavigate()
  const submitBasicInfo = useSubmitBasicInfo()
  const [error, setError] = useState<string | null>(null)

  const showTruckingInfo = isOwnerOperatorFlow(flow)
  const schema = useMemo(() => makeBasicInfoFormSchema(t, showTruckingInfo), [t, showTruckingInfo])

  const form = useForm<BasicInfoFormData>({
    resolver: standardSchemaResolver(schema),
    defaultValues,
  })
  const debouncedDot = useDebouncedDotNumber(form.control)
  const fmcsa = useFmcsaLookup(flow, token, debouncedDot)

  const onSubmit = (formData: BasicInfoFormData) => {
    setError(null)
    const isCompany = formData.hasCompany === true
    // The trucking fields are hidden for carrier drivers but still carry the office's
    // invite-time prefill; the PATCH replaces prefillFormData wholesale, so dropping
    // them here would erase the office's DOT (and with it the FMCSA insurance panel).
    submitBasicInfo.mutate(
      {
        flow,
        token,
        firstName: formData.firstName,
        lastName: formData.lastName,
        email: formData.email,
        phone: formData.phone || undefined,
        dateOfBirth: formData.dateOfBirth,
        cdlNumber: formData.cdlNumber || undefined,
        dotNumber: formData.dotNumber || undefined,
        companyName: isCompany ? formData.companyName || undefined : undefined,
        mcNumber: formData.mcNumber || undefined,
        truckCount: formData.truckCount,
        hasMultipleTrucks: formData.hasMultipleTrucks,
        serviceArea: formData.serviceArea || undefined,
        companyLegalName: isCompany ? formData.companyLegalName || undefined : undefined,
        ein: isCompany ? formData.ein || undefined : undefined,
        hasCompany: formData.hasCompany,
        physicalAddress: formData.physicalAddress?.trim() || undefined,
        // Hidden for carrier drivers, who never get the section — send an empty
        // set rather than `undefined` so clearing the last row actually deletes it.
        // Rows the applicant never filled are sent as-is: the server drops them
        // (`isDeclaredVehicle`, `ONB-23b`), and one definition of "declared" that
        // is authoritative beats two that can disagree.
        vehicles: toDeclaredVehicles(formData.vehicles),
      },
      {
        onSuccess: () => {
          navigate({
            to: '/onboard/$flow/$token/create-account',
            params: { flow, token },
            search: {
              email: formData.email,
              firstName: formData.firstName,
              lastName: formData.lastName,
              phone: formData.phone || undefined,
            },
          })
        },
        onError: (err: unknown) => {
          const reason = mapTokenErrorMessage(err)
          if (reason) {
            onTokenError(reason)
            return
          }
          const message = getErrorMessage(err, t('onboarding.error.generic'))
          setError(t(`onboarding.error.${message}`, { defaultValue: message }))
        },
      },
    )
  }

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-xl font-bold">{t('onboarding.title')}</h1>
        <p className="mt-1 text-sm text-muted-foreground">{t('onboarding.subtitle')}</p>
        {tenantName && (
          <p className="mt-2 text-sm text-foreground">
            {t('onboarding.invitedBy', { tenantName })}
          </p>
        )}
      </div>

      <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
        <OwnerOperatorDetailsFields
          form={form}
          showTruckingInfo={showTruckingInfo}
          lookup={{ dotNumber: debouncedDot, ...fmcsa }}
        />

        {error && (
          <div className="rounded-md bg-destructive/10 p-3 text-sm text-destructive">{error}</div>
        )}

        <Button type="submit" className="w-full" isDisabled={submitBasicInfo.isPending}>
          {submitBasicInfo.isPending && <LoaderCircleIcon className="animate-spin" />}
          {t('onboarding.continue')}
        </Button>
      </form>
    </div>
  )
}
